<script setup lang="ts">
import { HButton } from '@headless/atoms'

withDefaults(defineProps<{
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
}>(), {
  variant: 'primary',
  size: 'md',
  type: 'submit',  // 預設 submit，讓表單能正常提交
  disabled: false,
  loading: false,
  fullWidth: false,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()
</script>

<template>
  <HButton
    :disabled="disabled"
    :loading="loading"
    @click="emit('click', $event)"
    v-slot="{ disabled: isDisabled, loading: isLoading, click }"
  >
    <button
      :type="type"
      :disabled="isDisabled"
      :class="[
        'btn',
        `btn--${variant}`,
        `btn--${size}`,
        { 'btn--loading': isLoading },
        { 'btn--full-width': fullWidth },
      ]"
      @click="click"
    >
      <span v-if="isLoading" class="btn__spinner"></span>
      <span :class="{ 'btn__content--hidden': isLoading }">
        <slot />
      </span>
    </button>
  </HButton>
</template>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  font-family: var(--typography-font-family);
  font-weight: 500;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  position: relative;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn:focus-visible {
  outline: 2px solid var(--colors-primary);
  outline-offset: 2px;
}

/* Sizes */
.btn--sm {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--typography-body-small-size);
}

.btn--md {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--typography-body-default-size);
}

.btn--lg {
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--typography-body-large-size);
}

/* Variants */
.btn--primary {
  background-color: var(--colors-primary);
  color: var(--colors-text-inverse);
}

.btn--primary:hover:not(:disabled) {
  background-color: var(--colors-primary-hover);
}

.btn--secondary {
  background-color: var(--colors-secondary);
  color: var(--colors-text-inverse);
}

.btn--outline {
  background-color: transparent;
  border: 1px solid var(--colors-border-default);
  color: var(--colors-text-primary);
}

.btn--outline:hover:not(:disabled) {
  background-color: var(--colors-background-surface);
}

.btn--ghost {
  background-color: transparent;
  color: var(--colors-text-primary);
}

.btn--ghost:hover:not(:disabled) {
  background-color: var(--colors-background-surface);
}

.btn--danger {
  background-color: var(--colors-error);
  color: var(--colors-text-inverse);
}

.btn--full-width {
  width: 100%;
}

/* Loading */
.btn__spinner {
  position: absolute;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.btn__content--hidden {
  visibility: hidden;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
